import { useState, useMemo } from 'react';

import { toast } from 'react-toastify';

import {
    type TotemClosedDayFragmentFragment,
    useGetAllRegularTotemClosedDayQuery,
    useArchiveTotemClosedDayMutation,
} from 'data/__generated__';
import { DataTable } from 'components/DataTable';
import { Loader } from 'components/Loader';
import { confirmPopup } from 'components/Popups/Confirm';
import { formatDateAsAnniversary } from 'helpers/dateTimes';

import { totemClosedDayRemovedHandler } from '../cacheHandlers/totemClosedDayCacheHandler';
import { AbortTotemClosedDayPopup } from './AbortTotemClosedDayPopup';
import { getTotemClosedDaysColumns, type TotemClosedDayRow } from '../constants/totemClosedDaysColumns';

export const TotemClosedDaysList = () => {
    const [isAbortPopupOpen, setIsAbortPopupOpen] = useState<boolean>(false);
    const [totemClosedDayToAbort, setTotemClosedDayToAbort] = useState<TotemClosedDayFragmentFragment | null>(null);

    const {
        loading: totemClosedDaysLoading,
        data: totemClosedDaysData,
        error: totemClosedDaysError,
    } = useGetAllRegularTotemClosedDayQuery();

    const [archiveTotemClosedDay, { loading: archiveLoading }] = useArchiveTotemClosedDayMutation();

    const todayDateFormatted = formatDateAsAnniversary({ dateTime: new Date() });

    async function archive(totemClosedDayId: string) {
        if (
            await confirmPopup({
                title: 'Confirmer',
                message: 'Voulez-vous archiver ce jour de fermeture ?',
            })
        ) {
            const { data } = await archiveTotemClosedDay({ variables: { totemClosedDayId } });

            if (!data) {
                throw new Error('Une erreur est survenue lors de la création du jour de fermeture');
            }

            const { archiveTotemClosedDay: archivedTotemClosedDay } = data;

            if (archivedTotemClosedDay) {
                totemClosedDayRemovedHandler(archivedTotemClosedDay._id);
                toast.success('Jour de fermeture archivé');
            } else {
                toast.error('Une erreur est survenue');
            }
        }
    }

    const columns = useMemo(
        () =>
            getTotemClosedDaysColumns({
                todayDateFormatted,
                archiveLoading,
                onAbort: (totemClosedDay) => {
                    setTotemClosedDayToAbort(totemClosedDay);
                    setIsAbortPopupOpen(true);
                },
                onArchive: archive,
            }),
        [todayDateFormatted, archiveLoading],
    );

    if (totemClosedDaysLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Loader />
            </div>
        );
    }

    if (!totemClosedDaysData || totemClosedDaysError) {
        throw new Error('Une erreur est survenue lors de la récupération des jours de fermeture');
    }

    const { getAllRegularTotemClosedDay: totemClosedDays } = totemClosedDaysData;

    return (
        <>
            <div className="flex h-full w-full flex-col overflow-hidden">
                <DataTable<TotemClosedDayRow>
                    columns={columns}
                    data={totemClosedDays}
                    filterMode="client"
                    getRowId={(row) => row._id}
                    searchPlaceholder="Rechercher un jour de fermeture"
                    searchFilterKey="searchValue"
                />
            </div>
            {totemClosedDayToAbort ? (
                <AbortTotemClosedDayPopup
                    totemClosedDay={totemClosedDayToAbort}
                    isOpen={isAbortPopupOpen}
                    setIsOpen={setIsAbortPopupOpen}
                />
            ) : null}
        </>
    );
};
